import { useApi } from 'ApiHooks'
import { useBlockUI } from 'Context/BlockUIContext'
import { useErrorModal } from 'Context/ErrorModalContext'
import { Domicilio } from 'Interfaces/Domicilio'
import React, { useEffect, useState } from 'react'
import { Button, Modal, Form, Row, Col, Card, InputGroup } from 'react-bootstrap'

interface ModalEditarDomicilioProps {
    show: boolean
    domicilio?: Domicilio | null
    onClose: () => void
    onSave: (domicilio: Domicilio) => void
    setDomicilio?: any
    onCreate: (domicilio: Domicilio) => void
}

const emptyDomicilio = {
    calle: '',
    numero: '',
    piso: '',
    departamento: '',
    provincia: '',
    localidad: '',
    telefono: '',
    codigoPostal: '',
    referencias: '',
}

const ModalEditarDomicilio = ({
    show,
    domicilio,
    onClose,
    onSave,
    setDomicilio,
    onCreate
}: ModalEditarDomicilioProps) => {

    const api = useApi()
    const { showBlock, hideBlock } = useBlockUI()
    const { showError } = useErrorModal()
    const [form, setForm] = useState<any>(emptyDomicilio!)
    const [provincias, setProvincias] = useState<Array<{ value: string, label: string }>>([])
    const [localidades, setLocalidades] = useState<Array<{ value: string, label: string }>>([])
    const [sucursales, setSucursales] = useState<Array<{ value: string, label: string }>>([])
    const [validated, setValidated] = useState(false);

    const ObtenerSucursalesProvincias = async () => {
        showBlock("Cargando provincias...")
        try {
            const response = await api.getSucursalesProvincias()
            setProvincias(response.map((item: any) => ({
                value: item,
                label: item,
            })))
        } catch (error: any) {
            if (!api.isCancel(error)) {
                showError({
                    title: 'Error al cargar datos',
                    message: error?.response?.status === 401 ? 'Se ha vencido el tiempo de la sesión' : error?.response?.data?.message?.length > 0 ? error?.response?.data?.message : 'Error desconocido del servidor',
                })
            }
        } finally {
            hideBlock()
        }
    }

    const ObtenerSucursalesLocalidad = async (provincia: string) => {
        showBlock("Cargando localidades...")
        try {
            const response = await api.getSucursalesLocalidades(provincia)
            setLocalidades(response.map((item: any) => ({
                value: item,
                label: item,
            })))
        } catch (error: any) {
            if (!api.isCancel(error)) {
                showError({
                    title: 'Error al cargar datos',
                    message: error?.response?.status === 401 ? 'Se ha vencido el tiempo de la sesión' : error?.response?.data?.message?.length > 0 ? error?.response?.data?.message : 'Error desconocido del servidor',
                })
            }
        } finally {
            hideBlock()
        }
    }

    const ObtenerSucursalesAndreani = async (provincia: string, localidad: string) => {
        showBlock("Cargando sucursales...")
        try {
            const response = await api.getSucursalesAndreani(provincia, localidad)
            setSucursales(response.map((item: any) => ({
                value: item?.id,
                label: item?.descripcion + ' (' + item?.direccion_calle + ' ' + item?.direccion_numero + ')',
            })))
        } catch (error: any) {
            if (!api.isCancel(error)) {
                showError({
                    title: 'Error al cargar datos',
                    message: error?.response?.status === 401 ? 'Se ha vencido el tiempo de la sesión' : error?.response?.data?.message?.length > 0 ? error?.response?.data?.message : 'Error desconocido del servidor',
                })
            }
        } finally {
            hideBlock()
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setForm((prev: any) => ({ ...prev, [name]: value }))
    }

    const guardarDomicilio = () => {
        domicilio ?
            onSave(form) :
            onCreate(form)
        setLocalidades([])
        setProvincias([])
        setForm([])
        setSucursales([])
    }
    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        const formElement = e.currentTarget

        if (!formElement.checkValidity()) {
            e.stopPropagation()
            setValidated(true)
            return
        }
        guardarDomicilio()
    }

    useEffect(() => {
        ObtenerSucursalesProvincias();
        setForm(domicilio ?? emptyDomicilio);

        if (!domicilio) return;

        ObtenerSucursalesLocalidad(domicilio.provincia);

        if (show && domicilio.localidadSucursal) {
            ObtenerSucursalesAndreani(
                domicilio.provincia,
                domicilio.localidadSucursal
            );
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domicilio, show]);


    return (
        <Modal show={show} onHide={onClose} centered size="lg" style={{ backgroundColor: 'rgba(1,1,1,0.5)' }}
        >
            <Modal.Header closeButton>
                <Modal.Title style={{ color: 'white', fontWeight: 'bold' }}>
                    {domicilio ? 'Editar domicilio' : 'Nuevo domicilio'}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form noValidate validated={validated} onSubmit={handleSubmit}>
                    <Row>
                        <Col md={8}>
                            <Form.Group>
                                <Form.Label>Calle</Form.Label>
                                <Form.Control
                                    name="calle"
                                    value={form.calle}
                                    onChange={handleChange}
                                    required

                                />
                                <Form.Control.Feedback type="invalid">
                                    Campo obligatorio
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>

                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Número</Form.Label>
                                <Form.Control
                                    name="numero"
                                    value={form.numero}
                                    onChange={handleChange}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    Campo obligatorio
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Piso</Form.Label>
                                <Form.Control
                                    name="piso"
                                    value={form.piso}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={2}>
                            <Form.Group>
                                <Form.Label>Departamento</Form.Label>
                                <Form.Control
                                    name="departamento"
                                    value={form.departamento}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Col>

                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Código Postal</Form.Label>
                                <Form.Control
                                    name="codigoPostal"
                                    value={form.codigoPostal}
                                    onChange={handleChange}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    Obligatorio
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Teléfono</Form.Label>
                                <Form.Control
                                    name="telefono"
                                    value={form.telefono}
                                    onChange={handleChange}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    Campo obligatorio
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Provincia</Form.Label>

                                <InputGroup>
                                    <Form.Control
                                        as="select"
                                        name="provincia"
                                        required
                                        value={form?.provincia ?? ''}
                                        onChange={(e) => {
                                            handleChange(e);
                                            setLocalidades([])
                                            setSucursales([])
                                            if (e.target.value) {
                                                ObtenerSucursalesLocalidad(e.target.value);
                                            }
                                        }}
                                    >
                                        <option value="">Seleccionar</option>
                                        {provincias.map(u => (
                                            <option key={u.value} value={u.value}>
                                                {u.label}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </InputGroup>
                                <Form.Control.Feedback type="invalid">
                                    Campo obligatorio
                                </Form.Control.Feedback>
                            </Form.Group>

                        </Col>

                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Localidad</Form.Label>
                                <Form.Control
                                    name="localidad"
                                    value={form.localidad}
                                    onChange={handleChange}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    Campo obligatorio
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Card style={{ backgroundColor: 'white', width: '100%' }} >
                            <Card.Body>
                                <Card.Text style={{ fontWeight: 'bold', fontSize: 17 }}>
                                    <img src='/logo_andreani.svg' width={180} alt='logo_andreani' />
                                    Seleccione la sucursal Andreani.
                                </Card.Text>

                                <Row>

                                    <Col md={6}>
                                        <Form.Group>
                                            <div className="mb-1">
                                                <Form.Label>Localidad Sucursal</Form.Label>
                                                <Form.Control
                                                    as="select"
                                                    name='localidadSucursal'
                                                    value={form?.localidadSucursal || domicilio?.localidadSucursal || ''}
                                                    onChange={(e) => {
                                                        if (e?.target?.value !== '') {
                                                            handleChange(e)
                                                            ObtenerSucursalesAndreani(form?.provincia!, e?.target?.value)
                                                        }
                                                    }}
                                                >
                                                    <option value=''>Seleccionar</option>
                                                    {localidades.map(u => (
                                                        <option key={u.value} value={u.value}>
                                                            {u.label}
                                                        </option>
                                                    ))}
                                                </Form.Control>
                                            </div>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>Sucursal Andreani</Form.Label>

                                            <InputGroup>
                                                <Form.Control
                                                    as="select"
                                                    name='codigoSucursalCorreo'
                                                    value={form?.codigoSucursalCorreo || domicilio?.codigoSucursalCorreo || ''}
                                                    onChange={(e) => {
                                                        handleChange(e)
                                                    }}
                                                >
                                                    <option value={0}>Seleccionar</option>
                                                    {sucursales.map(u => (
                                                        <option key={u.value} value={u.value}>
                                                            {u.label}
                                                        </option>
                                                    ))}
                                                </Form.Control>

                                                {((form?.codigoSucursalCorreo !== 0 || domicilio?.codigoSucursalCorreo !== 0 || form?.codigoSucursalCorreo !== 0) && (domicilio?.codigoSucursalCorreo !== 0)) && (
                                                    <Button
                                                        variant="danger"
                                                        onClick={() => {
                                                            setForm((prev: any) => ({
                                                                ...prev,
                                                                codigoSucursalCorreo: 0
                                                            }))
                                                            setDomicilio((prev: any) => ({
                                                                ...prev,
                                                                codigoSucursalCorreo: 0
                                                            }))
                                                        }}
                                                        title="Borrar selección"
                                                    >
                                                        ✕
                                                    </Button>
                                                )}
                                            </InputGroup>

                                            <Form.Control.Feedback type="invalid">
                                                Campo obligatorio
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                    </Col>

                                </Row>
                            </Card.Body>
                        </Card>
                    </Row>
                    <Form.Group>
                        <Form.Label>Referencias</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            name="referencias"
                            value={form.referencias}
                            onChange={handleChange}
                        />
                    </Form.Group>
                    <div className="d-flex justify-content-end mt-3">
                        <Button variant="secondary" onClick={() => {
                            onClose()
                            setLocalidades([])
                            setProvincias([])
                            setForm([])
                            setSucursales([])
                        }}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type='submit' className='ml-3'>
                            {domicilio ? 'Guardar cambios' : 'Crear domicilio'}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>


        </Modal>
    )
}

export default ModalEditarDomicilio
